import { useId } from 'react'
import LanguageSelector from './shared/LanguageSelector'
import { getLangMeta, LANGUAGE_META as LANGS } from '../utils/languages'

interface Props {
  /** The deck's stored language code. */
  value: string
  /** The owner of a user deck may change it; everyone else reads it. */
  editable: boolean
  onChange?: (lang: string) => void
  disabled?: boolean
  labels: { label: string; note: string }
}

/**
 * A deck's language, on the deck screen. Fill writes Definitions and Examples
 * in it, so it is shown to every viewer and changed only by the owner of a
 * user deck. Changing it unlinks the deck's cards from the dictionary (ADR-070
 * Amendment 2) and rewrites nothing else; the owner is told, beside the
 * control, that existing cards keep their text until filled again.
 */
export function DeckLanguageField({ value, editable, onChange, disabled, labels }: Props) {
  const id = useId()
  const meta = getLangMeta(value)
  // A stored code outside the picker's list stays selectable as itself.
  const options = Array.from(new Set([value, ...Object.keys(LANGS)]))
  return (
    <div data-deck-language style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span id={id} style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{labels.label}</span>
        {editable ? (
          <span aria-busy={disabled}>
            <LanguageSelector
              value={value}
              options={options}
              onChange={(l) => { if (!disabled && l !== value) onChange?.(l) }}
              ariaLabel={labels.label}
            />
          </span>
        ) : (
          <span aria-labelledby={id} style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{meta.label}</span>
        )}
      </div>
      {editable && (
        <p style={{ margin: 0, fontSize: 12, lineHeight: 1.45, color: 'var(--text-secondary)', maxWidth: 460 }}>
          {labels.note}
        </p>
      )}
    </div>
  )
}
